import React, { useCallback } from "react";
import { useQuery, useMutation } from "@apollo/client";
import useToggle from "react-use/lib/useToggle";

import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { useRouteMatch, Link } from "react-router-dom";

import { toast } from "react-toastify";
import {
  getClientFeatureQuery,
  QUERY_SHELLY_CONTROLLERS,
} from "../../../../config/graphql/query";
import {
  DELETE_SHELLY_CONTROLLER,
  MUTATION_SWITCH_SHELLY_CONTROLLER,
  TOGGLE_CLIENT_FEATURE,
} from "../../../../config/graphql/mutation";

import Table from "../../../../components/Table";

import useHasRole from "../../../../lib/hooks/useHasRole";
import { useClientId } from "../hooks/useClient";

const TableRow = ({
  item,
  index,
}: {
  item: IShellyController;
  index: number;
}) => {
  const { url } = useRouteMatch();

  const clientId = useClientId();
  const isAdmin = useHasRole(["ADMIN"]);
  const isClientAdmin = useHasRole(["CLIENT_ADMIN"]);

  const { t } = useTranslation(["client", "common"]);

  const { id, role, title, deviceId, type, code, gen, timeout } = item;

  const [visible, setVisible] = useToggle(false);

  const [onDelete] = useMutation(DELETE_SHELLY_CONTROLLER, {
    variables: { id },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
    refetchQueries: [
      {
        query: QUERY_SHELLY_CONTROLLERS,
        context: {
          headers: {
            ClientId: clientId,
          },
        },
      },
    ],
  });

  const onBeforeDelete = () => {
    onDelete()
      .then(() => {
        toast.success<string>(t("client:client.shelly.toast.deleted"));
      })
      .catch(() => {
        toast.error<string>(t("client:client.shelly.toast.deletedError"));
      });

    setVisible(false);
  };

  const [onSwitch] = useMutation(MUTATION_SWITCH_SHELLY_CONTROLLER, {
    variables: { input: { id, action: "ON_WITH_DELAY" } },
    context: {
      headers: {
        ClientId: clientId,
      },
    },
  });

  const onBeforeSwitch = () => {
    onSwitch()
      .then(() => {
        if (role === "DOOR") {
          toast.success<string>(t("client:client.shelly.toast.doorOpened"));
        }
        if (role === "BELL") {
          toast.success<string>(t("client:client.shelly.toast.bellActivated"));
        }
      })
      .catch(() => {
        if (role === "DOOR") {
          toast.error<string>(t("client:client.shelly.toast.doorOpenedError"));
        }
        if (role === "BELL") {
          toast.error<string>(
            t("client:client.shelly.toast.bellActivatedError"),
          );
        }
      });

    setVisible(false);
  };

  return (
    <>
      <tr>
        <th scope="row">{index + 1}</th>
        <td>{title}</td>
        <td>{role}</td>
        <td>{deviceId}</td>
        <td>{type.toUpperCase()}</td>
        <td>{code}</td>
        <td>{gen}</td>
        <td>{timeout} s</td>
        <td>
          <div className="d-flex">
            {(isAdmin || isClientAdmin) && (
              <>
                <Button
                  size="sm"
                  variant="danger"
                  className="mr-2"
                  onClick={() => setVisible(true)}
                >
                  {t("common:delete")}
                </Button>
                <Link to={`${url}/${id}`}>
                  <Button size="sm" variant="primary" className="mr-2">
                    {t("client:client.shelly.button.view")}
                  </Button>
                </Link>
              </>
            )}
            <Button
              size="sm"
              variant="success"
              className="mr-2"
              onClick={onBeforeSwitch}
            >
              {role === "DOOR" && t("client:client.shelly.button.openDoor")}
              {role === "BELL" && t("client:client.shelly.button.ringBell")}
            </Button>
          </div>
        </td>
      </tr>
      <Modal
        size="sm"
        show={visible}
        onHide={setVisible}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("client:client.shelly.modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("client:client.shelly.modal.body", { id })}</Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setVisible(false)}>
            {t("common:cancel")}
          </Button>
          <Button size="sm" variant="danger" onClick={onBeforeDelete}>
            {t("common:delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const ShellyControllers = React.memo(() => {
  const match = useRouteMatch();

  const { t } = useTranslation(["client", "common"]);

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);
  const isClientAdmin = useHasRole(["CLIENT_ADMIN"]);

  const { data } = useQuery<{ shellyControllers: IShellyController[] }>(
    QUERY_SHELLY_CONTROLLERS,
    {
      context: {
        headers: {
          ClientId: clientId,
        },
      },
      fetchPolicy: "network-only",
    },
  );

  const shellyControllers = data?.shellyControllers ?? [];

  const renderShellyControllers = useCallback(
    (item: IShellyController, index: number) => (
      <TableRow key={item.id} item={item} index={index} />
    ),
    [],
  );

  return (
    <div className="tab-content">
      <div className="d-flex justify-content-between align-items-center my-4">
        <h5 className="ml-3 align-self-end mb-1">
          {t("client:client.shelly.title")}
        </h5>
        {(isAdmin || isClientAdmin) && (
          <div>
            <Link
              to={`${match.url}/new`}
              type="button"
              className="btn btn-primary"
            >
              {t("client:client.shelly.button.add")}
            </Link>
          </div>
        )}
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">{t("client:client.shelly.th.title")}</th>
            <th scope="col">{t("client:client.shelly.th.role")}</th>
            <th scope="col">{t("client:client.shelly.th.deviceId")}</th>
            <th scope="col">{t("client:client.shelly.th.type")}</th>
            <th scope="col">{t("client:client.shelly.th.code")}</th>
            <th scope="col">{t("client:client.shelly.th.gen")}</th>
            <th scope="col">{t("client:client.shelly.th.timeout")}</th>
            <th scope="col">{t("client:client.tokens.th.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {!!shellyControllers.length &&
            shellyControllers.map(renderShellyControllers)}
          {!shellyControllers.length && (
            <th colSpan={9} scope="row" className="text-center">
              {t("client:client.shelly.info.noControllers")}
            </th>
          )}
        </tbody>
      </Table>
      {!!shellyControllers.length && (
        <p className="text-right text-danger">
          {t("client:client.shelly.hint")}
        </p>
      )}
    </div>
  );
});

export const ToggleFeatureButton: React.FC<{
  disable?: boolean;
  onClick: () => void;
  featureName: string;
}> = React.memo(({ disable, onClick, featureName }) => {
  const { t } = useTranslation(["common"]);
  return (
    <button
      className={`btn btn-primary ${disable ? "btn-danger" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
      }}
    >
      {t(`common:${disable ? "disable" : "enable"}`)} {featureName}
    </button>
  );
});

const ShellyFeature: React.FC = () => {
  const { t } = useTranslation(["common"]);

  const clientId = useClientId();

  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery("shelly"), {
    context: {
      headers: {
        ClientId: clientId,
      },
    },
    variables: { id: clientId },
    fetchPolicy: "network-only",
  });

  const [updateShelly] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      { query: getClientFeatureQuery("shelly"), variables: { id: clientId } },
    ],
  });

  const shellyEnabled = data?.client?.preferences?.shelly?.enabled ?? false;

  const onToggleShelly = useCallback(
    async (enabled: boolean) => {
      try {
        await updateShelly({
          variables: {
            input: {
              id: clientId,
              feature: "shelly",
              enabled,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}`, {
            feature: "Shelly",
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}Error`, {
            feature: "Shelly",
          }),
        );
      }
    },
    [clientId, updateShelly, t],
  );

  if (!shellyEnabled) {
    return (
      <div className="tab-pane fade show active" id="shelly" role="tabpanel">
        {isAdmin ? (
          <ToggleFeatureButton
            onClick={() => onToggleShelly(true)}
            featureName="Shelly"
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-pane fade show active" id="shelly" role="tabpanel">
      <ShellyControllers />
      {isAdmin && (
        <div className="mt-3">
          <ToggleFeatureButton
            disable
            onClick={() => onToggleShelly(false)}
            featureName="Shelly"
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(ShellyFeature);
